import type { Entity } from '@backstage/catalog-model';

export type C4ElementKind =
  | 'Person'
  | 'SoftwareSystem'
  | 'Container'
  | 'Component'
  | 'Database'
  | 'ExternalSystem';

export interface C4Color {
  fill: string;
  stroke: string;
  text: string;
}

const DATABASE_RESOURCE_TYPES = [
  'database',
  'cache-database',
  'sql',
  'postgres',
  'datastore',
];

function normalize(value: unknown): string | undefined {
  return typeof value === 'string' ? value.trim().toLowerCase() : undefined;
}

function getSpecType(entity: Entity): string | undefined {
  return normalize((entity?.spec as { type?: unknown } | undefined)?.type);
}

export function isDatabaseResource(entity: Entity): boolean {
  const type = getSpecType(entity);
  return type !== undefined && DATABASE_RESOURCE_TYPES.includes(type);
}

export function getC4ElementKind(entity: Entity): C4ElementKind {
  const kind = normalize(entity?.kind);
  const type = getSpecType(entity);

  switch (kind) {
    case 'domain':
      return 'SoftwareSystem';
    case 'system':
      return type === 'external' ? 'ExternalSystem' : 'SoftwareSystem';
    case 'component':
      return 'Container';
    case 'resource':
      return isDatabaseResource(entity) ? 'Database' : 'Container';
    case 'api':
      return 'Component';
    case 'group':
    case 'user':
      return 'Person';
    default:
      return 'Container';
  }
}

export function getC4TypeLabel(entity: Entity): string {
  const kind = normalize(entity?.kind);

  if (kind === 'domain') {
    return 'Domain';
  }

  switch (getC4ElementKind(entity)) {
    case 'Person':
      return 'Person';
    case 'SoftwareSystem':
    case 'ExternalSystem':
      return 'System';
    case 'Database':
      return 'Database';
    case 'Component':
      return 'Component';
    case 'Container':
    default:
      return 'Container';
  }
}

const PALETTE: Record<C4ElementKind, C4Color> = {
  Person: { fill: '#08427B', stroke: '#052E56', text: '#FFFFFF' },
  SoftwareSystem: { fill: '#1168BD', stroke: '#0B4884', text: '#FFFFFF' },
  ExternalSystem: { fill: '#999999', stroke: '#6B6B6B', text: '#FFFFFF' },
  Container: { fill: '#438DD5', stroke: '#2E6295', text: '#FFFFFF' },
  Component: { fill: '#85BBF0', stroke: '#5D82A8', text: '#0B2E4F' },
  Database: { fill: '#B8621B', stroke: '#804413', text: '#FFFFFF' },
};

export function getC4Color(entity: Entity): C4Color {
  return { ...PALETTE[getC4ElementKind(entity)] };
}
